import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusCircle, MinusCircle, PenLine, CheckCircle2, ArrowRight } from "lucide-react";

const COLORS = {
  added: "#34D399",
  removed: "#F87171",
  modified: "#FBBF24",
  unchanged: "#52525B",
};

export default function ComparisonResult({ comparison }) {
  const { result, baseline_name, current_name } = comparison;
  const { summary, added, removed, modified } = result;

  return (
    <div className="space-y-6" data-testid="comparison-result">
      <div className="font-mono text-xs text-zinc-500 flex items-center gap-2">
        <span className="text-zinc-300">{baseline_name}</span>
        <ArrowRight className="w-3.5 h-3.5" />
        <span className="text-zinc-300">{current_name}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard label="Total Changes" value={summary.total_changes} color="#3B82F6" testid="metric-total" big />
        <MetricCard label="Added" value={summary.added} color={COLORS.added} icon={PlusCircle} testid="metric-added" />
        <MetricCard label="Removed" value={summary.removed} color={COLORS.removed} icon={MinusCircle} testid="metric-removed" />
        <MetricCard label="Modified" value={summary.modified} color={COLORS.modified} icon={PenLine} testid="metric-modified" />
        <MetricCard label="Unchanged" value={summary.unchanged} color={COLORS.unchanged} icon={CheckCircle2} testid="metric-unchanged" />
      </div>

      {summary.by_type?.length > 0 && (
        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">
            Change distribution by asset type
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={summary.by_type} barGap={2}>
              <CartesianGrid strokeDasharray="2 2" stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="type" stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <YAxis stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0C0C0C", border: "1px solid #2A2A2A", borderRadius: 4, fontFamily: "JetBrains Mono", fontSize: 12 }}
                cursor={{ fill: "rgba(255,255,255,0.03)" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="added" fill={COLORS.added} />
              <Bar dataKey="removed" fill={COLORS.removed} />
              <Bar dataKey="modified" fill={COLORS.modified} />
              <Bar dataKey="unchanged" fill={COLORS.unchanged} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <Tabs defaultValue="modified">
        <TabsList className="bg-[#101010] border border-zinc-800 rounded-sm p-1">
          <TabsTrigger value="modified" data-testid="diff-tab-modified" className="rounded-sm font-mono text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400">
            Modified ({summary.modified})
          </TabsTrigger>
          <TabsTrigger value="added" data-testid="diff-tab-added" className="rounded-sm font-mono text-xs data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-400">
            Added ({summary.added})
          </TabsTrigger>
          <TabsTrigger value="removed" data-testid="diff-tab-removed" className="rounded-sm font-mono text-xs data-[state=active]:bg-red-500/15 data-[state=active]:text-red-400">
            Removed ({summary.removed})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="modified">
          {modified.length === 0 ? (
            <Empty text="No modified assets." />
          ) : (
            <div className="space-y-3">
              {modified.map((m, i) => (
                <ModifiedRow key={i} item={m} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="added">
          {added.length === 0 ? <Empty text="No added assets." /> : <AssetList assets={added} tone="added" />}
        </TabsContent>

        <TabsContent value="removed">
          {removed.length === 0 ? <Empty text="No removed assets." /> : <AssetList assets={removed} tone="removed" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ label, value, color, icon: Icon, testid, big }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2" style={{ color }}>
        {Icon && <Icon className="w-4 h-4" />}
        <span className="font-mono text-[11px] uppercase tracking-wider">{label}</span>
      </div>
      <div className={`font-display font-bold ${big ? "text-3xl" : "text-2xl"}`} style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-zinc-500 font-mono py-8 text-center">{text}</p>;
}

function ModifiedRow({ item }) {
  return (
    <div className="border border-amber-500/20 bg-amber-500/[0.04] rounded-sm overflow-hidden" data-testid="diff-row-modified">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-amber-500/15">
        <div className="flex items-center gap-2 font-mono text-xs">
          <PenLine className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-amber-400">{item.asset_id}</span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-400">{item.type}</span>
        </div>
        <span className="font-mono text-[10px] text-zinc-500">
          {item.changes.length} field change{item.changes.length === 1 ? "" : "s"}
        </span>
      </div>
      <table className="w-full font-mono text-xs">
        <tbody>
          {item.changes.map((c, i) => (
            <tr key={i} className="border-b border-zinc-900 last:border-0">
              <td className="px-4 py-2 text-zinc-400 w-1/4">{c.field}</td>
              <td className="px-4 py-2 w-3/8">
                <span className="line-through text-red-400/80">{c.old ?? "—"}</span>
              </td>
              <td className="px-2 py-2 text-zinc-600 text-center w-8">
                <ArrowRight className="w-3 h-3 inline" />
              </td>
              <td className="px-4 py-2">
                <span className="text-emerald-400">{c.new ?? "—"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetList({ assets, tone }) {
  const styles =
    tone === "added"
      ? "border-emerald-500/20 bg-emerald-500/[0.04]"
      : "border-red-500/20 bg-red-500/[0.04]";
  const idColor = tone === "added" ? "text-emerald-400" : "text-red-400";
  return (
    <div className="space-y-2">
      {assets.map((a, i) => (
        <div key={i} className={`border ${styles} rounded-sm px-4 py-3`} data-testid={`diff-row-${tone}`}>
          <div className="flex items-center gap-2 font-mono text-xs mb-2">
            <span className={idColor}>{a.asset_id}</span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-400">{a.type}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(a.fields)
              .filter(([k]) => k !== "ADACId")
              .map(([k, v]) => (
                <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400">
                  {k}=<span className="text-zinc-200">{v}</span>
                </span>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
